import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PpobService from 'service/PPOB/PpobService';
import { PpobTransactionHistoryCmsResp } from '../../domain/payload/response/PPOB/PpobResp';
import CustomDropdown, { DropdownOption } from 'component/CustomDropdown';
import DateRangeFilterDropdown from 'component/DateRangeFilterDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';
import PpobHistoryDetailModal from './PpobHistoryDetailModal';

const sourceAppOptions: DropdownOption[] = [
  { id: '', name: 'All Source App' },
  { id: 'Perfect Store', name: 'Perfect Store' },
  { id: 'More Access', name: 'More Access' },
];

const PpobHistory: React.FC = () => {
  const { profile } = useSelector((state: any) => state.auth);
  const [data, setData] = useState<PpobTransactionHistoryCmsResp[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [sourceApp, setSourceApp] = useState<string | number | null>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedTxId, setSelectedTxId] = useState<string | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const [timeRange, setTimeRange] = useState<{ startDate: string | null; endDate: string | null }>({
    startDate: null,
    endDate: null,
  });

  const ppobService = PpobService();

  const fetchData = async () => {
    const token = profile?.data?.token;
    if (!token) return;

    setLoading(true);
    try {
      const res = await ppobService.getTransactionHistory(
        token,
        searchKeyword.trim() ? searchKeyword : null,
        timeRange.startDate != null && timeRange.startDate !== '' ? timeRange.startDate : null,
        timeRange.endDate != null && timeRange.endDate !== '' ? timeRange.endDate : null,
        sourceApp ? String(sourceApp) : null,
        currentPage,
        pageSize
      );
      setData(res.data ?? []);
      setTotalPages(res.pagination?.totalPage ?? 1);
      setTotalRecords(res.pagination?.totalRecords ?? 0);
    } catch (err: any) {
      console.error('Error fetching PPOB transaction history:', err);
      const errorMessage = typeof err === 'string' ? err : err.message || 'Terjadi kesalahan saat mengambil data history PPOB';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [searchKeyword, timeRange, sourceApp, currentPage, pageSize]);

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSearchKeyword(searchTerm);
    setCurrentPage(1);
  };

  const handleResetFilter = () => {
    setSearchTerm('');
    setSearchKeyword('');
    setTimeRange({ startDate: null, endDate: null });
    setSourceApp('');
    setCurrentPage(1);
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      maximumFractionDigits: 0,
    }).format(val || 0);
  };

  const getStatusBadge = (status: string, label: string) => {
    switch (status) {
      case 'SUCCESS':
        return <span className="badge bg-success">{label || 'Sukses'}</span>;
      case 'PENDING':
        return <span className="badge bg-info text-dark">{label || 'Diproses'}</span>;
      case 'WAITING_PAYMENT':
        return <span className="badge bg-warning text-dark">{label || 'Menunggu'}</span>;
      case 'EXPIRED':
        return <span className="badge bg-secondary">{label || 'Kedaluwarsa'}</span>;
      case 'FAILED':
        return <span className="badge bg-danger">{label || 'Gagal'}</span>;
      case 'CANCELLED':
        return <span className="badge bg-danger">{label || 'Dibatalkan'}</span>;
      default:
        return <span className="badge bg-secondary">{label || status}</span>;
    }
  };

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>PPOB Transaction History</h5>
            <div>
              <p>TransMore Transactions</p>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="p-4" style={{ background: 'white' }}>
            <div className="mb-3 d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
              <form className="d-flex flex-column flex-sm-row" onSubmit={handleSearchSubmit}>
                <input
                  type="text"
                  className="form-control form-control-compact me-2 mb-2 mb-sm-0"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ minWidth: 160 }}
                />
                <button type="submit" className="px-4 btn btn-primary">
                  <i className="fa-solid fa-search"></i>
                </button>
              </form>

              <div className="d-flex flex-wrap gap-2 align-items-center">
                <DateRangeFilterDropdown
                  align="right"
                  onChange={(v) => {
                    setTimeRange({ startDate: v.startDate, endDate: v.endDate });
                    setCurrentPage(1);
                  }}
                />

                <CustomDropdown
                  value={sourceApp}
                  options={sourceAppOptions}
                  onChange={(opt) => {
                    setSourceApp(opt.id);
                    setCurrentPage(1);
                  }}
                  placeholder="All Source App"
                />
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-condensed table-hover">
                <thead className="table-primary">
                  <tr>
                    <th>ID</th>
                    <th>No. Transaction</th>
                    <th>User</th>
                    <th>Source App</th>
                    <th>Product / Item</th>
                    <th className="text-end">Total Payment</th>
                    <th>Created At</th>
                    <th className="text-center">Status</th>
                    <th className="text-center"></th>
                  </tr>
                </thead>
                <tbody>
                  {!loading ? (
                    data.length > 0 ? (
                      data.map((item) => (
                        <tr key={item.id}>
                          <td>{item.id}</td>
                          <td>{item.transactionNo}</td>
                          <td>
                            <div>{item.userName || '-'}</div>
                            <small className="text-muted">{item.userEmail || item.userPhone || '-'}</small>
                          </td>
                          <td>
                            {item.sourceApp ? (
                              <span className="badge bg-light text-dark border">
                                <i className="fa-solid fa-mobile-screen me-1 text-primary"></i>
                                {item.sourceApp}
                              </span>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>
                            <div>{item.itemName || item.productName || '-'}</div>
                            <small className="text-muted">{item.groupName}</small>
                          </td>
                          <td className="text-end">{formatCurrency(item.totalPayment)}</td>
                          <td>{item.createdAt}</td>
                          <td className="text-center">{getStatusBadge(item.status, item.statusLabel)}</td>
                          <td className="text-center">
                            <button
                              className="border-0 btn btn-outline-info btn-small-compact"
                              title="View Detail"
                              onClick={() => {
                                setSelectedTxId(item.id);
                                setShowDetailModal(true);
                              }}
                            >
                              <i className="fa-solid fa-eye"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9} className="text-center">
                          Tidak ada data
                        </td>
                      </tr>
                    )
                  ) : (
                    <tr>
                      <td colSpan={9} className="text-center">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-3 d-flex justify-content-end align-items-center">
              <div className="d-flex align-items-center">
                <div className="me-4 d-flex align-items-center">
                  <label className="me-2">Rows per page:</label>
                  <CustomDropdown
                    value={pageSize}
                    options={PageSizeOption.options}
                    onChange={(opt) => {
                      setPageSize(Number(opt.id));
                      setCurrentPage(1);
                    }}
                    align="top"
                  />
                </div>
                <span className="me-3">
                  {totalRecords > 0
                    ? `${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`
                    : '0 data'}
                </span>

                <nav>
                  <ul className="mb-0 pagination">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>
                    <li className="page-item disabled">
                      <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                    </li>
                    <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>

          </div>
        </div>
      </div>

      <PpobHistoryDetailModal
        show={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        transactionId={selectedTxId}
      />
    </div>
  );
};

export default PpobHistory;
